import React, { useEffect, useState } from "react";
import SearchInput from "../SearchInput/SearchInput";
import ResultsList from "../ResultsList/ResultsList";
import { searchCpv, getCpv, sendUserSearch } from "../../services/cpvService";
import LoadMore from "../SearchInput/LoadMore";
import { Box } from "@mui/material";
import _, { flatMap } from "lodash";
import {
  getCached,
  getAllCache,
  storeCache,
  removeCache,
  clearCache,
} from "../../services/cacheService";

const debouncedSave = _.debounce(
  (searchValue, selectedNode) => sendUserSearch({ searchValue, selectedNode }),
  1500
);

const Search = ({ setHasSearchResults }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [cpvs, setCpvs] = useState([]);
  const [caches, setCaches] = useState(getAllCache());
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingResults, setLoadingResults] = useState(true);
  const [selectedCpvItems, setSelectedCpvItems] = useState(getAllCache());
  const debouncedSearchKey = useDebounce(searchKey, 500);

  useEffect(() => {
    const cachedItems = getAllCache();
    setSelectedCpvItems(cachedItems);
  }, []);

  useEffect(() => {
    localStorage.setItem("cpvcodes", JSON.stringify(selectedCpvItems));
  }, [selectedCpvItems]);

  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(timer);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const reloadSearch = async () => {
    try {
      setLoadingResults(true);
      const sanitizedSearchKey = debouncedSearchKey.match(
        /^[a-zA-Z0-9\säöüÄÖÜßẞ]+$/g
      );
      if (!sanitizedSearchKey) {
        setError("No CPV code found");
        setLoading(false);
        return;
      }
      const { data } = await searchCpv(sanitizedSearchKey);
      setHasMore(false);
      if (!Array.isArray(data) && !data?.error) {
        setError("No CPV code found");
      } else {
        setSearchResults(data);
        setError(data?.error);
        setHasSearchResults(data.length > 0);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  const handleSearch = () => {
    if (!debouncedSearchKey) return;
    setLoading(true);
    setCpvs([]);
    reloadSearch();
  };
  const handleGetter = async (node) => {
    setSelected(node);
    setLoadingResults(false);
    setLoading(true);
    setSearchResults([]);
    await getCpv(node.code).then((res) => {
      setLoading(false);
      setHasMore(
        res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
      );
      setCpvs(res.data);
      setError(res.data?.error);
    });
  };
  const handleSearchClick = async () => {
    setLoadingResults(false);
    setCpvs([]);
    if (debouncedSearchKey.length == 0) {
      setLoading(true);
      setSearchResults([]);
      setPage(0);
      await getCpv("listAll").then((res) => {
        setHasMore(
          res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
        );
        setError("");
        setLoading(false);
        setCpvs(res.data);
      });
    } else if (
      debouncedSearchKey.length > 0 &&
      debouncedSearchKey.length <= 2
    ) {
      return;
    } else {
      const sanitizedSearchKey = debouncedSearchKey.match(
        /^[a-zA-Z0-9\säöüÄÖÜßẞ]+$/g
      );
      if (!sanitizedSearchKey) {
        setError("No CPV code found");
        setLoading(false);
        return;
      }
      setLoading(true);
      setSearchResults([]);
      setPage(0);
      await getCpv(debouncedSearchKey).then((res) => {
        setHasMore(
          res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
        );
        setLoading(false);
        setCpvs(res.data);
        setError(res.data?.error);
      });
    }
  };
  const handleCachedItem = (item, isChecked) => {
    if (isChecked)
      sendUserSearch({ searchValue: searchKey, selectedNode: item.code });
    if (!!getCached(item.code)) {
      removeCache(item.code);
      setCaches(getAllCache());
      return false;
    }
    const data = {
      code: item.code,
      description: item.description,
    };

    storeCache(data);
    setCaches(getAllCache());
    return true;
  };
  const handleLoadMore = async () => {
    setLoading(true);
    const key = searchKey || "listAll";
    await getCpv(key, page).then((res) => {
      if (key == "listAll") {
        setHasMore(
          res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
        );
      } else {
        setHasMore(
          res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
        );
      }
      const data = res.data;
      setCpvs((prev) => [...prev, ...data]);
      setLoading(false);
    });
  };

  const clearSearchResult = (tag) => {
    if (tag == "remove") {
      setSearchKey("");
      setCpvs([]);
      setError("");
    }
    setSearchResults([]);
  };

  const handleSearchInputChange = (e) => {
    const searchValue = e.target.value;
    setSearchKey(searchValue);

    if (searchValue.length > 0) {
      debouncedSave(searchValue, null);
    }
  };

  // useEffect(() => {
  //   setSelected(null);
  //   handleSearch(searchKey);
  // }, [searchKey]);

  useEffect(() => {
    setSelected(null);
    handleSearch(debouncedSearchKey);
  }, [debouncedSearchKey]);

  useEffect(() => {
    clearCache();
  }, []);

  useEffect(() => {
    if (cpvs.length > 0) handleLoadMore();
  }, [page]);

  return (
  <>
    <SearchInput
      loading={loading}
      searchKey={searchKey}
      handleSearchInputChange={handleSearchInputChange}
      searchItems={searchResults}
      handleSearchClick={handleSearchClick}
      clearSearchResult={clearSearchResult}
      errorMessage={error}
      getItem={handleGetter}
      caches={caches}
      handleCachedItem={handleCachedItem}
      data={cpvs}
      hasMore={hasMore}
    />
    {cpvs.length > 0 && !loadingResults && (
      <Box className="cpv-search-list-wrapper">
        {searchKey.length > 2 || error === "" ? (
          <>
            <ResultsList
              data={cpvs}
              handleCachedItem={handleCachedItem}
              caches={caches}
              searchKey={debouncedSearchKey}
              selected={selected}
              searchItems={searchResults}
              loading={loading}
            />
            {!loadingResults && hasMore && (
              <LoadMore
                hasMore={hasMore}
                setPage={setPage}
                loading={loading}
              />
            )}
          </>
        ) : null}
      </Box>
    )}
  </>
);
};
export default Search;
